import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["item"]
  static values = { url: String }

  dragStart(event) {
    if (this.isProcessing) return

    this.draggedItem = this.#itemContaining(event.target)
    if (!this.draggedItem) return

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.dropEffect = "move"
    event.dataTransfer.setData("text/plain", this.draggedItem.dataset.id)

    requestAnimationFrame(() => {
      this.draggedItem.dataset.dragging = ""
    })
  }

  dragOver(event) {
    if (!this.draggedItem) return
    event.preventDefault()

    const targetItem = this.#itemContaining(event.target)
    if (!targetItem || targetItem === this.draggedItem) {
      this.#clearDropIndicators()
      return
    }

    this.#clearDropIndicators()

    if (this.#isAboveTarget(targetItem, event.clientY)) {
      targetItem.dataset.dropAbove = ""
    } else {
      targetItem.dataset.dropBelow = ""
    }
  }

  dragLeave(event) {
    const targetItem = this.#itemContaining(event.target)
    if (targetItem && !targetItem.contains(event.relatedTarget)) {
      delete targetItem.dataset.dropAbove
      delete targetItem.dataset.dropBelow
    }
  }

  drop(event) {
    event.preventDefault()

    const targetItem = this.#itemContaining(event.target)
    if (!targetItem || targetItem === this.draggedItem) {
      this.#clearDropIndicators()
      return
    }

    const insertAbove = this.#isAboveTarget(targetItem, event.clientY)
    const newPosition = this.#calculateNewPosition(targetItem, insertAbove)
    this.#moveItem(targetItem, insertAbove)
    this.#clearDropIndicators()

    this.#submitPositionUpdate(newPosition)
  }

  dragEnd() {
    if (this.draggedItem) {
      delete this.draggedItem.dataset.dragging
    }
    this.#clearDropIndicators()
    this.draggedItem = null
  }

  #itemContaining(element) {
    return this.itemTargets.find(item => item.contains(element) || item === element)
  }

  #clearDropIndicators() {
    this.itemTargets.forEach(item => {
      delete item.dataset.dropAbove
      delete item.dataset.dropBelow
    })
  }

  #isAboveTarget(targetItem, cursorY) {
    const rect = targetItem.getBoundingClientRect()
    return cursorY < rect.top + rect.height / 2
  }

  #moveItem(targetItem, insertAbove) {
    const sibling = insertAbove ? targetItem : targetItem.nextSibling
    targetItem.parentNode.insertBefore(this.draggedItem, sibling)
  }

  #calculateNewPosition(targetItem, insertAbove) {
    const targetIndex = this.itemTargets.indexOf(targetItem)
    const draggedIndex = this.itemTargets.indexOf(this.draggedItem)

    if (insertAbove) {
      return draggedIndex < targetIndex ? targetIndex : targetIndex + 1
    } else {
      return draggedIndex < targetIndex ? targetIndex + 1 : targetIndex + 2
    }
  }

  async #submitPositionUpdate(newPosition) {
    this.isProcessing = true

    const id = this.draggedItem.dataset.id
    const url = this.urlValue.replace("__id__", id)

    const body = new FormData()
    body.append("position", newPosition)

    const response = await patch(url, { body })
    this.isProcessing = false
    if (response.ok) {
      Turbo.visit(window.location.href, { action: "replace" })
    }
  }
}
